import { Box } from '@mui/material';
import CustomIcons from '../../assests/CustomIcons';

export interface FormValues {
  widget_id?: string;
  name: string;
  address: string;
  location: {
    lat: number | null;
    lng: number | null;
  };
  map_height: number;
  map_zoom: string;
  show_customer_name: boolean;
  show_address_disclaimer: boolean;
  show_moment_dates: boolean;
  obfuscate_review_type: 'show_author' | 'show_initials' | 'show_custom_name';
  moments_per_page: number;
  custom_author_name?: string;
  background_color?: string;
  widget_layout: 'default' | 'map-only' | 'no-map';
  moment_list_layout?: 'slider' | 'tiles';
  moment_card_media_position: 'top' | 'bottom' | 'left' | 'right';
  date_format: string;
  map_marker_icon: string;
  map_marker_color: string;
  slide_per_view: number;
  font_family: string;
  card_background_type: 'flat' | 'gradient';
  theme_type: 'light' | 'dark' | 'custom';
  theme_name?: string;
  moment_card_font_size: string;
  gradient_rotation: number;
  gradient_first_color: string;
  gradient_first_stop: number;
  gradient_second_color: string;
  gradient_second_stop: number;
  is_custom_pallete: boolean;
  card_background_color: string;
  text_color: string;
  buisness_color: string;
  category_color: string;
  category_bg_color: string;
  border_color: string;
  no_data_image: string | File;
  map_marker_icon_url: string | any;
  is_all_border_radius: string;
  border_radius: number;
  border_top_left_radius: number;
  border_top_right_radius: number;
  border_bottom_left_radius: number;
  border_bottom_right_radius: number;
  border_width: number;
  border_style: string;
  map_marker_color_background: string;
  is_agency_branding: boolean;
  agency_branding_text: string;
  agency_branding_link: string;
  is_filter_by_category: boolean;
  selected_category: string[];
  is_filter_by_locality: boolean;
  selected_localities: string[];
}

export const BorderOption = [
  {
    value: 'none',
    label: (
      <Box
        sx={{
          width: 26,
          height: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          mt: 0.5,
          fontWeight: 500
        }}
      >
        ×
      </Box>
    )
  },
  {
    value: 'solid',
    label: (
      <Box
        sx={{
          width: 26,
          border: '1px solid #000',
          mt: 0.5
        }}
      />
    )
  },
  {
    value: 'dashed',
    label: (
      <Box
        sx={{
          width: 26,
          border: '1px dashed #000',
          mt: 0.5
        }}
      />
    )
  },
  {
    value: 'dotted',
    label: (
      <Box
        sx={{
          width: 26,
          border: '1px dotted #000',
          mt: 0.5
        }}
      />
    )
  }
];

export const menuItemSX = {
  /* ---------- Size & Spacing ---------- */
  minHeight: '36px !important',
  py: '6px !important',
  px: '12px !important',
  mx: '4px !important',
  my: '2px !important',
  borderRadius: 'var(--small-border-radius) !important',

  fontWeight: '400 !important',
  fontSize: '14px !important',
  fontFamily: 'var(--font-family-manrope)',

  '&:hover, &.Mui-focused, &[data-focus="true"]': {
    backgroundColor: '#f5f5f5 !important',
    boxShadow: 'none !important'
  },

  '&.Mui-selected, &[aria-selected="true"]': {
    backgroundColor: '#E8F0FE !important',
    color: '#1a73e8 !important',
    fontWeight: '500 !important',
    boxShadow: 'none !important',
    '& *': {
      color: '#1a73e8 !important',
      fontWeight: '500 !important'
    }
  },

  '&.Mui-selected:hover, &[aria-selected="true"]:hover, &.Mui-focused[aria-selected="true"], &[data-focus="true"][aria-selected="true"]': {
    backgroundColor: '#E8F0FE !important',
    boxShadow: 'none !important',
    color: '#1a73e8 !important',
    '& *': {
      color: '#1a73e8 !important',
      fontWeight: '500 !important'
    }
  },

  /* ---------- Edge Items ---------- */
  '&:first-of-type': { mt: '3px !important' },
  '&:last-of-type': { mb: '3px !important' }
};

export const obfuscateReviewTypeOptions = [
  { value: 'show_author', label: 'Show author' },
  { value: 'show_initials', label: 'Show initials' },
  { value: 'hide_name', label: 'Hide Name' }
];

// Put this outside the component
export const THEME_CONFIG: {
  [bgType: string]: {
    [themeType: string]: Record<string, any>;
  };
} = {
  flat: {
    light: {
      card_background_color: '#ffffff',
      text_color: '#000000',
      buisness_color: '#252525',
      category_color: '#000000',
      category_bg_color: '#edf0f4',
      border_color: '#dcdcdc'
    },

    gray: {
      card_background_color: '#e1e0e0',
      text_color: '#000000',
      buisness_color: '#252525',
      category_color: '#000000',
      category_bg_color: '#f3f3f3',
      border_color: '#dcdcdc'
    },

    dark: {
      card_background_color: '#4b4b4b',
      text_color: '#ffffff',
      buisness_color: '#f3f3f3',
      category_color: '#ffffff',
      category_bg_color: '#3c3c3c',
      border_color: '#5f5f5f'
    }
  },

  gradient: {
    light: {
      card_background_color: 'transparent',
      text_color: '#000000',
      buisness_color: '#252525',
      category_color: '#000000',
      category_bg_color: '#fcfcfc',
      border_color: '#dcdcdc',

      gradient_rotation: 325,
      gradient_first_color: '#fffde9',
      gradient_first_stop: 18,
      gradient_second_color: '#ffc5ea',
      gradient_second_stop: 100
    },

    gray: {
      card_background_color: '#f5f5f5',
      text_color: '#1a1a1a',
      buisness_color: '#333333',
      category_color: '#2b2b2b',
      category_bg_color: '#efefef',
      border_color: '#c9c9c9',

      gradient_rotation: 145,
      gradient_first_color: '#e0e0e0',
      gradient_first_stop: 0,
      gradient_second_color: '#bdbdbd',
      gradient_second_stop: 100
    },

    dark: {
      card_background_color: 'transparent',
      text_color: '#ffffff',
      buisness_color: '#f3f3f3',
      category_color: '#ffffff',
      category_bg_color: '#3c3c3c',
      border_color: '#5f5f5f',

      gradient_rotation: 145,
      gradient_first_color: '#2D0505',
      gradient_first_stop: 0,
      gradient_second_color: '#136a8a',
      gradient_second_stop: 81
    }
  }
};

export const GOOGLE_RED_PIN_URL = 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';

export const iconMap: any = {
  pin: <CustomIcons.Location style={{ width: 30, height: 30 }} fill="#000000" />,
  flag: <CustomIcons.MarkerFlag style={{ width: 30, height: 30 }} />,
  star: <CustomIcons.MarkerStar style={{ width: 30, height: 30 }} />,
  house: <CustomIcons.MarkerHouse style={{ width: 30, height: 30 }} />,
  heart: <CustomIcons.MarkerHeart style={{ width: 30, height: 30 }} />,
  store: <CustomIcons.MarkerStore style={{ width: 30, height: 30 }} />,
  custom: <CustomIcons.CustomMarker style={{ width: 30 }} fill="#000000" />
};
